/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  AvcCycleContext,
  IntervalEnum,
} from '@ben/api/gen/codegen.schemas';
import type { TagColorVariant } from '@ben/orion/constants/display-status';
import type { OutputValueType } from '@ben/orion/utils/calculated-value';
import type { RJSFSchema } from '@rjsf/utils';
import type { CustomComponentTypes } from '../constants';
import type { ApprovalStep } from './approval-step';
import type { DefaultDeductionSourceEnum, PaymentSourceEnum } from './benefit';
import type { EnrollmentStatus } from './benefit-enrollment';
import type { BenefitFeature, SliderValueType } from './columbia';
import type { RestrictionSet } from './restriction-set';

// New Modular Benefit

interface IOptOutReasonChoices {
  code: string;
  description: string;
  allowCustomReason: boolean;
}

interface ModularBenefitTabs {
  showEnrolments: boolean;
  showChanges: boolean;
  showPaymentHistory: boolean;
  showRequests: boolean;
}

export interface RequestSchema {
  title: string;
  description?: string;
  schema: RJSFSchema;
}

export enum BenefitRequestType {
  PRE_CLEARANCE = 'PRE_CLEARANCE',
  AVC = 'AVC',
  PENSION_HOLIDAY = 'PENSION_HOLIDAY',
}

export type PensionHolidayStatus =
  | 'SCHEDULED'
  | 'ACTIVE'
  | 'COMPLETED'
  | 'ENDED_EARLY'
  | 'CANCELLED';

export interface PensionHolidayActiveHoliday {
  id: number;
  status: PensionHolidayStatus;
  startDate: string;
  endDate: string | null;
  requestedStartDate: string | null;
  requestedDurationMonths: number | null;
  previousSelection: Record<string, unknown>;
}

export interface PensionHolidayAwaitingResume {
  id: number;
  resumesOn: string;
  previousSelection: Record<string, unknown>;
}

export interface PensionHolidayContext {
  isEnabled: boolean;
  isEligible: boolean;
  ineligibilityReasonCode: string | null;
  requiresApproval: boolean;
  benefitRequestConfigId: number | null;
  durationMode: string;
  allowedDurationsMonths: number[];
  fixedDurationMonths: number | null;
  allowIndefinite: boolean;
  earliestStartDate: string;
  openRequest: unknown | null;
  activeHoliday: PensionHolidayActiveHoliday | null;
  awaitingResume: PensionHolidayAwaitingResume | null;
  avcAvailable: boolean;
}

export interface ModularBenefitRequestConfig {
  id: number;
  configId?: number;
  cta?: {
    platformUrl: string;
    platformName: string;
    buttonText?: string;
    entityName?: string;
  };
  bannerText: string;
  schema: RequestSchema;
  windows?: IModularBenefitEnrolmentWindow[];
  allowConcurrentRequests?: boolean;
  requestType: BenefitRequestType;
}

export interface ModularBenefitCustomModal {
  content: string;
  ctaText: string;
  disclaimer: string;
  id: number;
  title: string;
  type: 'OPT_IN' | 'OPT_OUT' | 'CHANGE';
}

export interface IEmployeeBenefit {
  benefit: IModularBenefit;
  enrolment: IModularEnrolment;
  features: BenefitFeature[];
  canOptIn: boolean;
  canOptOut: boolean;
  canUpdate: boolean;
  canWaive: boolean;
  optOutReasonChoices: IOptOutReasonChoices[];
  tabs: ModularBenefitTabs;
  benefitRequests?: ModularBenefitRequestConfig[];
  avcCycleContext?: AvcCycleContext | null;
  pensionHoliday?: PensionHolidayContext | null;
  customModals?: ModularBenefitCustomModal[];
  optInCopy?: string;
  optOutCopy?: string;
  optInCopyPreposition?: string;
  optOutCopyPreposition?: string;
  canFallbackToSalary?: boolean;
}

export interface IModularBenefit {
  id: number;
  name: string;
  showPrice: boolean;
  showPaymentSources?: boolean;
  showTotalLoanBanner?: boolean;
  benefitProductType?: string | null;
  newBenefitType?: string;
  paymentSource: PaymentSourceEnum;
  defaultDeductionSource?: DefaultDeductionSourceEnum;
  missingRequirements: any[];
  benefitRequirements: string[];
  additionalConfig: IModularBenefitAdditionalConfig;
  imageUrl: string;
  imageAltText?: string;
  heroImageUrl?: string;
  allowance: IModularBenefitAllowance;
  content: string;
  ctaText: string;
  externalLinkUrl: string;
  documents: IModularBenefitDocument[];
  optInButtonText?: string;
  hasWaiverGroup?: boolean;
  allowEmployeePayDeductionChoice?: boolean;
  closeWindowsOnFulfillment?: boolean;
  hideClosedConfigWindowBanner?: boolean;
  grossPayDeductionTitle?: string | null;
  grossPayDeductionDescription?: string | null;
  netPayDeductionTitle?: string | null;
  netPayDeductionDescription?: string | null;
}

export enum WindowTypeEnum {
  CONFIG_WINDOW = 'CONFIG_WINDOW',
  OPT_OUT_WINDOW = 'OPT_OUT_WINDOW',
  OPT_IN_WINDOW = 'OPT_IN_WINDOW',
  LOCKED_WINDOW = 'LOCKED_WINDOW',
}

export enum WindowSubTypeEnum {
  NONE = 'NONE',
  MINIMUM_COMMITMENT_TERM = 'MINIMUM_COMMITMENT_TERM',
}

export enum StartsOnEnum {
  SPECIFIC_DATE = 'SPECIFIC_DATE',
  EMPLOYMENT_START = 'EMPLOYMENT_START',
  ELIGIBILITY_START = 'ELIGIBILITY_START',
  PROBATION_END = 'PROBATION_END',
  POSTPONEMENT_END = 'POSTPONEMENT_END',
  OPT_IN = 'OPT_IN',
  ENROLLMENT_END = 'ENROLLMENT_END',
  BEFORE_ENROLLMENT_END = 'BEFORE_ENROLLMENT_END',
  BENEFIT_START_DATE = 'BENEFIT_START_DATE',
  LIFE_EVENT = 'LIFE_EVENT',
  AGE_THRESHOLD = 'AGE_THRESHOLD',
}

export interface IModularBenefitAllowance {
  id: number;
  name: string;
  amount: string;
  currency: string;
  amountAvailable: string;
  amountAvailableNextRenewal: string;
  cardId: string;
  renewalPeriod?: IntervalEnum;
}

export interface IModularBenefitAdditionalConfig {
  id: number;
  steps: IModularBenefitStep[];
  polymorphicCtype: number;
  beginSetupText: string;
  componentsInFormulas?: {
    pricing?: (string | number)[];
    [componentId: string]: (string | number)[] | undefined;
  };
}

export interface IModularBenefitStep {
  id: number;
  name: string;
  description: string;
  components: IModularBenefitComponent[];
}

// All the Modular Benefit Components should include the following properties
interface ModularBenefitComponentBase {
  id: number;
  name: string;
  showName?: boolean;
  description?: string;
  key: string;
  required: boolean;
  gatesApprovalFlow?: boolean;
  value?: string | number | boolean | unknown;
  defaultValue?: string | number | boolean | unknown;
  default?: string | number | boolean | unknown;
  enrolmentWindows?: IModularBenefitEnrolmentWindow[];
  hasActiveWindowExclusion?: boolean;
  restrictionSet?: RestrictionSet;
  disabled?: boolean;
  hideForEmployee?: boolean;
  hideForEcc?: boolean;
}

// This is a type that includes all the possible properties of a ModularBenefitComponent
interface ModularBenefitComponentAll extends ModularBenefitComponentBase {
  // If you type a component, exclude it's own type from below so you can enjoy the type narrowing
  type: Exclude<
    CustomComponentTypes,
    CustomComponentTypes.MULTI_SELECT_WITH_SUBFORM
  >;
  placeholder?: string;
  maxLength?: number;
  label?: string;
  showLabel?: boolean;
  searchPlaceholder?: string;
  showSearch?: boolean;
  options?: IModularBenefitSelectOption[] | IModularBenefitMultiSelectOption[];
  employeeDefault?: number;
  employerMax?: number;
  employerMin?: number;
  contacts?: IModularBenefitContact[];
  beneficiaries?: IModularBenefitBeneficiary[];
  acceptedContactTypes?: string[];
  sliderValueType?: SliderValueType;
  min?: number;
  max?: number;
  /** BUY_SELL_HOLIDAY only: formula-aware sell capacity from the API. */
  sellMax?: number;
  outputValueType?: OutputValueType;
  outputValueSymbol?: string; // Can be a `%` character or a currency code
  unitType?: 'DAYS' | 'HOURS';
  hasCalculatedFormula?: boolean;
}

export interface ModularBenefitMultiSelectWithSubformValue {
  key: string;
  values?: Record<string, unknown>;
}

export interface DocumentSigningSigner {
  role: string;
  order: number;
  status: string;
}

export interface DocumentSigningValue {
  signingRequestId?: number;
  status?: string;
  documentType?: string;
  signedDocumentUrl?: string | null;
  signedAt?: string | null;
  signers?: DocumentSigningSigner[];
}

export interface ModularBenefitMultiSelectWithSubform extends ModularBenefitComponentBase {
  type: CustomComponentTypes.MULTI_SELECT_WITH_SUBFORM;
  options: IModularBenefitMultiSelectOption[];
  value?: ModularBenefitMultiSelectWithSubformValue[];
  min?: number;
  max?: number;
}

export type IModularBenefitComponent =
  | ModularBenefitComponentAll
  | ModularBenefitMultiSelectWithSubform;

export interface IModularBenefitContact {
  id: number;
  firstName: string;
  lastName: string;
  contactType: string;
  disabled: boolean;
  subComponents: IModularBenefitComponent[];
  disabledReasons: string[];
  contactTypeKey: string;
}

// A row of the component's `beneficiaries` list: the picker source for people
// who already hold a beneficiary record. `linkedContact` points at the
// matching `contacts` row.
export interface IModularBenefitBeneficiary {
  id: string;
  type: string;
  name: string;
  relationship: string;
  relationshipLabel: string;
  linkedContact: number;
  disabled: boolean;
}

export interface IModularBenefitSelectOption {
  key: string;
  label: string;
  title?: string;
}

export interface IModularBenefitMultiSelectOption {
  key: string;
  title: string;
  description: string;
  label?: string;
  subComponents: IModularBenefitComponent[];
}

export interface IBenefitEnrolmentDisplayStatus {
  text: string;
  type: TagColorVariant;
}

export enum EnrolmentType {
  OPT_IN = 'OPT_IN',
  OPT_OUT = 'OPT_OUT',
  LOCKED = 'LOCKED',
  DISCOUNT = 'DISCOUNT',
}

export interface ModularBenefitBanner {
  header: string;
  body: string;
  date?: string;
  cancellable?: boolean;
}

export interface IModularEnrolment {
  id: number;
  isActive: boolean;
  status: EnrollmentStatus;
  updatedTimestamp: string;
  enrolledAddons: any[];
  policyNumber: any;
  configWindowOpen: boolean;
  enrolmentWindows: IModularBenefitEnrolmentWindow[];
  contributionSources: any[];
  priceIsEstimate: boolean;
  additionalInfo: any;
  optOutReason?: string;
  displayStatus: IBenefitEnrolmentDisplayStatus;
  approvalSteps: ApprovalStep[];
  generatedAllowance?: string;
  isAvailable: boolean;
  enrolmentType: EnrolmentType;
  optInTimestamp?: string;
  benefitStartDate?: string;
  optOutTimestamp?: string;
  benefitEndDate?: string;
  contactEnrolments?: IModularContactEnrolment[];
  employeeMonthlyPrice?: string;
  employerMonthlyPrice?: string;
  currency?: string;
  autoEnrolmentBanner?: ModularBenefitBanner;
  optInByAdmin?: boolean;
  lockedWindowOpen?: boolean;
  discountCode?: string;
  payDeductionType?: string;
}

export interface IModularContactEnrolment {
  firstName: string;
  lastName: string;
  contactType: string;
  displayStatus: IBenefitEnrolmentDisplayStatus;
  isActive: boolean;
  isAvailable: boolean;
  status: EnrollmentStatus;
  updatedTimestamp: string;
  policyNumber?: string;
  optOutReason?: string;
  optInTimestamp?: string;
  benefitStartDate?: string;
  benefitEndDate?: string;
  optInByAdmin?: boolean;
}

export interface IModularBenefitEnrolmentWindow {
  windowType: string;
  windowSubType?: string;
  startTimestamp: string;
  endTimestamp: string;
  isOpen: boolean;
}

// Modular Benefit Price

export interface IModularBenefitPrice {
  total: IModularBenefitPriceTotal;
  breakdown: IModularBenefitPriceBreakdown[];
  contributionSources: IModularBenefitPriceContributionSources;
  priceIsEstimate?: boolean;
  periodContext?: ModularBenefitPricePeriodContext;
  selectionState?: ModularBenefitPriceSelectionState;
  /**
   * Only returned on admin (ECC) price requests, and null when there is nothing
   * to report. Advisory only — admins are warned, never blocked.
   */
  nmwImpact?: ModularBenefitNmwImpact | null;
}

export type ModularBenefitNmwStatus =
  | 'PASS'
  | 'FAIL'
  | 'AT_RISK'
  | 'NOT_APPLICABLE'
  | 'ERROR';

export type ModularBenefitNmwLikelyCause =
  | 'MISSING_DOB'
  | 'MISSING_SALARY'
  | 'MISSING_COUNTRY'
  | 'MISSING_CONTRACTED_HOURS'
  | 'ZERO_CONTRACTED_HOURS'
  | 'MISSING_WAGE_CONFIG'
  | 'AGE_BAND_CHANGE'
  | 'NMW_RATE_CHANGE'
  | 'SALARY_CHANGE'
  | 'FTE_CHANGE'
  | 'CONTRACTED_HOURS_CHANGE'
  | 'COUNTRY_CHANGE'
  | 'SALARY_DEDUCTIONS'
  | 'CURRENCY_MISMATCH';

export interface ModularBenefitNmwImpact {
  status: ModularBenefitNmwStatus;
  likelyCause: ModularBenefitNmwLikelyCause | null;
  recommendedAction: string | null;
  shortfallAmount: string | null;
  shortfallCurrency: string | null;
  projectedHourlyRate: string | null;
  minHourlyRate: string | null;
}

export interface IModularBenefitPriceTotal {
  employee: IModularBenefitTotalPrice;
  employer: IModularBenefitTotalPrice;
}

export type NullablePriceAmount = number | string | null;

export interface IModularBenefitTotalPrice {
  amount: NullablePriceAmount;
  currency: string;
  previousAmount?: NullablePriceAmount;
}

export interface IModularBenefitPriceBreakdown {
  employee: IModularBenefitTotalPrice;
  employer: IModularBenefitTotalPrice;
  name: string;
}

export interface IModularBenefitPriceContributionSources {
  salary: ModularBenefitPriceContributionSource;
  allowance: ModularBenefitPriceContributionSource;
}

export type ModularBenefitPriceContributionSourceType =
  | 'RECURRING'
  | 'IMMEDIATE_DEDUCTION';

export interface ModularBenefitPriceContributionSource extends IModularBenefitTotalPrice {
  type?: ModularBenefitPriceContributionSourceType;
}

export interface ModularBenefitPricePeriodContext {
  allowanceCycleInterval: string;
  currentPeriodStartDate: string;
  currentPeriodEndDate: string;
  nextPeriodStartDate: string;
}

export interface ModularBenefitPriceSelectionState {
  status?: string;
  immediateDeduction?: boolean;
  renewalWindowExpired?: boolean;
  isReselectionWindow?: boolean;
  hasFutureSubscription?: boolean;
  effectiveFrom?: string | null;
  currentUntil?: string | null;
  currentEmployeePriceOnReselection?: NullablePriceAmount;
}

export interface IModularBenefitDocument {
  name: string;
  description: string;
  file: string;
}
